import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import { PermissionsInterface } from "../sessionSlice";
import { fetchAdmins, createAdmin, updateAdmin, destroyAdmin } from "../../actions/watcher/adminsActions";

export interface AdminRecordInterface {
  id: number;

  username: string;
  email: string;
  role: string;

  avatar_url?: string | null;
  permissions?: PermissionsInterface;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';

  editId: EntityId | null;
  editError: string | null;

  newParams: Partial<AdminRecordInterface> | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null
};

const adaptor = createEntityAdapter<AdminRecordInterface>();

const slice = createSlice({
  name: 'admins',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchAdmins.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchAdmins.fulfilled, (state, action) => {
        state.entities = action.payload.admins;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchAdmins.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #create
      .addCase(createAdmin.fulfilled, (state, action) => {
        const admin = Object.values(action.payload.admin)[0];
        adaptor.addOne(state, admin);
      })
      .addCase(createAdmin.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updateAdmin.fulfilled, (state, action) => {
        const admin = Object.values(action.payload.admin)[0];
        adaptor.upsertOne(state, admin);
      })
      .addCase(updateAdmin.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #destroy
      .addCase(destroyAdmin.fulfilled, (state, action) => {
        adaptor.removeOne(state, action.payload.id);
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setEditError,
  setNewParams,
  setNewError
} = slice.actions;

export const {
  selectById: selectAdminById,
  selectIds: selectAdminIds,
  selectEntities: selectAdminEntities,
}  = adaptor.getSelectors<RootState>(state => state.watcher.admins);
